import dayjs from 'dayjs'

// Importing the pt-br locale
import 'dayjs/locale/pt-br'

// Extending DayJS plugins
import localizedFormat from 'dayjs/plugin/localizedFormat'
import customParseFormat from 'dayjs/plugin/customParseFormat'
import utc from 'dayjs/plugin/utc'
import minMax from 'dayjs/plugin/minMax'
import isBetween from 'dayjs/plugin/isBetween'

// Setting pt-br as the default locale
// NOTE: This is a global setting, consider using the locale parameter in the format method, if you need to format dates in different locales
//
// https://day.js.org/docs/en/i18n/i18n
// https://day.js.org/docs/en/display/format#locale
dayjs.locale('pt-br')

// Extending DayJS plugins
dayjs.extend(localizedFormat)
dayjs.extend(customParseFormat)
dayjs.extend(utc)
dayjs.extend(minMax)
dayjs.extend(isBetween)

type DateType = string | number | Date | null | undefined

type DateFormats =
  | 'L' // Format L as defined by Locale. e.g. 'DD/MM/YYYY' for pt-BR or 'MM/DD/YYYY' for en-US
  | 'MM/YY'
  | 'MM/YYYY'
  | 'DD/MM'
  | 'DD/MM/YY'
  | 'DD/MM/YYYY'
  | 'MMM' // Jan-Dec
  | 'MMMM' // January-December
  | 'YYYY'
  | 'YYYYMM'
  | 'YYYY-MM'
  | 'YYYY-MM-DD'
  | 'YYYY-MM-DD HH:mm:ss'
  | 'DD/MM/YYYY HH:mm:ss'
  | 'HH:mm'
  | 'HH[h]mm'
  | 'YYYY-MM-DDTHH:mm:ssZ[Z]'
  | 'YYYY-MM-DDTHH:mm:ss'

type DateUnits = 'day' | 'week' | 'month' | 'year' | 'days' | 'weeks' | 'months' | 'years'

/**
 * @param date Date to be formatted
 * @param format {DateFormats} Format to be returned
 * @returns Formatted date
 * @default format 'L' (Output format)
 * @default inputFormat undefined (Input format)
 */
const formatDate = (
  date: DateType,
  format: DateFormats = 'L',
  inputFormat?: DateFormats
) => {
  if (!date || date === '') {
    return ''
  }

  if (inputFormat) {
    return dayjs(date, inputFormat).format(format)
  }

  return dayjs(date).format(format)
}

const formatServerDate = (date?: DateType) => {
  if (!date) {
    date = new Date()
  }

  return formatDate(date, 'YYYY-MM-DD HH:mm:ss')
}

const formatSendDate = (date?: DateType) => {
  if (!date) {
    return ''
  }

  return formatDate(date, 'DD/MM/YY')
}

const formatCoteDate = (date: string | Date) => {
  return dayjs(date, 'MM/YYYY').format('MM/YY')
}

/**
 * Compares two dates considering the first day of the month and returns true if they are one year apart.
 * @param initialQuota Date
 * @param finalQuota Date
 * @returns boolean
 */
const oneYearApart = (initialQuota: DateType, finalQuota: DateType) => {
  if (!initialQuota || !finalQuota) {
    return false
  }

  const normalizedInitialQuota = dayjs.utc(initialQuota).startOf('month')
  const normalizedFinalQuota = dayjs.utc(finalQuota).startOf('month')
  const oneYearLater = dayjs.utc(normalizedInitialQuota).add(1, 'year').toDate()

  return dayjs(normalizedFinalQuota).diff(oneYearLater) <= 1
}

const getPreviousYearDate = (currentDate: Date) => {
  return dayjs.utc(currentDate).subtract(1, 'year').toISOString()
}

const getFirstDayOfMonth = (date?: DateType, format: DateFormats = 'L') => {
  return dayjs.utc(date).startOf('month').format(format)
}

const getLastDayOfMonth = (date?: DateType, format: DateFormats = 'L') => {
  return dayjs.utc(date).endOf('month').format(format)
}

const getDiffBetweenDates = (startDate: DateType, endDate: DateType, unit: DateUnits) => {
  return dayjs.utc(endDate).diff(dayjs(startDate), unit)
}

// TODO: Needs to refactor
const dateAdd = (
  date: DateType,
  amount: number,
  unit: DateUnits,
  format: DateFormats = 'L',
  inputFormat: DateFormats = 'L'
) => {
  if (inputFormat === 'YYYY-MM-DDTHH:mm:ssZ[Z]') {
    date = dayjs(date).toISOString()
  }

  return dayjs.utc(date, inputFormat).add(amount, unit).format(format)
}

// TODO: Needs to refactor
const dateSubtract = (
  date: DateType,
  amount: number,
  unit: DateUnits,
  format: DateFormats = 'L',
  inputFormat: DateFormats = 'L'
) => {
  if (inputFormat === 'YYYY-MM-DDTHH:mm:ssZ[Z]') {
    date = dayjs(date).toISOString()
  }

  return dayjs.utc(date, inputFormat).subtract(amount, unit).format(format)
}

// NOTE: When date is undefined, it is considered as the current date
const isDateBefore = (date: Date | string | undefined, compareDate: Date | string) => {
  return dayjs.utc(date).isBefore(dayjs.utc(compareDate))
}

// NOTE: When date is undefined, it is considered as the current date
const isDateAfter = (date: Date | string | undefined, compareDate: Date | string) => {
  return dayjs.utc(date).isAfter(dayjs.utc(compareDate))
}

/**
 * Parameter 4 is a string with two characters; '[' means inclusive, '(' exclusive
 * '()' excludes start and end date
 * '[]' includes start and end date (default)
 * '[)' includes the start date but excludes the stop
 * '(]' excludes the start date but includes the stop
 *
 * Granuality offers the precision on start and end inclusive checks.
 * For example including the start date on day precision you should use 'day' as 3rd parameter.
 * https://day.js.org/docs/en/plugin/is-between
 **/
const isDateBetween = (
  date: DateType,
  startDate: DateType,
  endDate: DateType,
  precision: '()' | '[]' | '[)' | '(]' = '[]'
) => {
  return dayjs
    .utc(date)
    .isBetween(dayjs.utc(startDate), dayjs.utc(endDate), null, precision)
}

const wrapperDateObject = (date: Date | string, format: DateFormats = 'L') => {
  return dayjs(date, format) as unknown as string
}

const getMaxDate = (dates: Date[]) => {
  return dayjs.max(dates.map((date) => dayjs(date)))?.toDate()
}

export {
  formatServerDate,
  formatSendDate,
  formatCoteDate,
  oneYearApart,
  getPreviousYearDate,
  formatDate,
  getFirstDayOfMonth,
  getLastDayOfMonth,
  getDiffBetweenDates,
  dateAdd,
  dateSubtract,
  isDateBefore,
  isDateAfter,
  isDateBetween,
  wrapperDateObject,
  getMaxDate
}
